import { useApolloClient } from '@apollo/client';
import { Space, Button, message } from 'antd';
import { Formik } from 'formik';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type RoleSpecsFragment } from '../../../api/fragments/RoleSpecs';
import {
    AttachUserOnRoleDocument,
    type AttachUserOnRoleMutation,
    type AttachUserOnRoleMutationVariables,
} from '../../../api/mutations/attachUserOnRole';
import DrawerWithAutoWidth from '../../../components/DrawerWithAutoWidth';
import useHandleError from '../../../utilities/useHandleError';
import useValidator from '../../../utilities/useValidator';
import validators from '../../../utilities/validators';
import RoleAddUserForm from './RoleAddUserForm';

export type FormValues = { users: string[] };

const initialValues: FormValues = { users: [] };

export type RoleAddUserDrawerProps = {
    role: RoleSpecsFragment;
    onClose: () => void;
    visible: boolean;
};

const formValidator = validators.compose(validators.requiredArray('users'));

const RoleAddUserDrawer = ({ role, onClose, visible }: RoleAddUserDrawerProps) => {
    const { t } = useTranslation(['roleDetails']);
    const apolloClient = useApolloClient();
    const roleId = role.id;

    const [isFormValid, setIsFormValid] = useState<boolean>(false);

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('roleDetails:userDrawer.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<AttachUserOnRoleMutation, AttachUserOnRoleMutationVariables>({
                    mutation: AttachUserOnRoleDocument,
                    variables: { roleId, userIds: values.users },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('roleDetails:userDrawer.messages.success'),
                key: 'primary',
            });

            // then close the drawer
            onClose();
        },
        [apolloClient, t, onClose, roleId]
    );

    const validate = useValidator(formValidator);

    return (
        <DrawerWithAutoWidth
            extra={
                <Space>
                    <Button onClick={onClose}>{t('roleDetails:userDrawer.cancelButton')}</Button>
                    <Button disabled={!isFormValid} form="addUsers" htmlType="submit" type="primary">
                        {t('roleDetails:userDrawer.submitButton')}
                    </Button>
                </Space>
            }
            onClose={onClose}
            title={t('roleDetails:userDrawer.title')}
            visible={visible}
            destroyOnClose
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate} validateOnMount>
                {({ handleSubmit }) => (
                    <RoleAddUserForm onSubmit={handleSubmit} role={role} setIsFormValid={setIsFormValid} />
                )}
            </Formik>
        </DrawerWithAutoWidth>
    );
};

export default RoleAddUserDrawer;

export const useAddUserDrawer = (role: RoleAddUserDrawerProps['role']) => {
    const [visible, setVisible] = useState(false);
    const actions = useMemo(
        () => ({
            open: () => setVisible(true),
            close: () => setVisible(false),
        }),
        [setVisible]
    );

    return {
        ...actions,
        render: () => <RoleAddUserDrawer onClose={actions.close} role={role} visible={visible} />,
    };
};
